import type { MenuList } from '../../interface/layout/menu.interface';
import type { FC } from 'react';

import { Button, Drawer, Space, Switch, Tooltip } from 'antd';
import { HolderOutlined, ReloadOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { CustomIcon } from './customIcon';

interface Props {
  open: boolean;
  onClose: () => void;
  items: MenuList;
  hiddenCodes: string[];
  onToggleHidden: (code: string) => void;
  onReorder: (fromCode: string, toCode: string) => void;
  onReset: () => void;
}

const SidebarSettingsDrawer: FC<Props> = ({
  open,
  onClose,
  items,
  hiddenCodes,
  onToggleHidden,
  onReorder,
  onReset,
}) => {
  const { locale } = useSelector((state: any) => state.user);
  const [draggedCode, setDraggedCode] = useState<string | null>(null);

  return (
    <Drawer
      title="Customize sidebar"
      placement="right"
      open={open}
      onClose={onClose}
      extra={
        <Tooltip title="Reset to default">
          <Button icon={<ReloadOutlined rev={undefined} />} onClick={onReset} size="small" />
        </Tooltip>
      }
    >
      <p style={{ color: '#888', marginTop: 0, marginBottom: 16 }}>
        Drag to reorder. Toggle to show or hide an item.
      </p>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        {items.map((item: any) => {
          const isHidden = hiddenCodes.includes(item.code);
          return (
            <div
              key={item.code}
              draggable
              onDragStart={() => setDraggedCode(item.code)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (draggedCode && draggedCode !== item.code) onReorder(draggedCode, item.code);
                setDraggedCode(null);
              }}
              onDragEnd={() => setDraggedCode(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                cursor: 'grab',
                opacity: draggedCode === item.code ? 0.5 : 1,
                background: isHidden ? 'rgba(0,0,0,0.03)' : 'transparent',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <HolderOutlined rev={undefined} style={{ color: '#999' }} />
                {item.icon && <CustomIcon type={item.icon} />}
                {item.label?.[locale] || item.label?.en_US || item.code}
              </span>
              <Switch
                checked={!isHidden}
                onChange={() => onToggleHidden(item.code)}
                size="small"
              />
            </div>
          );
        })}
      </Space>
    </Drawer>
  );
};

export default SidebarSettingsDrawer;
